<template>
  <div>
    <!-- 画像追加ダイアログ -->
    <v-dialog v-model="imageDialog.show" width="677" persistent class="uploading-dialog">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>画像追加</v-card-title>
        <v-card-text>
          <o-upload
            v-if="!imageDialog.mediaFile"
            class="upload-part"
            :accept="allowedExtensions"
            drag-drop
            @update:model-value="loadFile"
          >
            <section class="section">
              <div class="content has-text-centered">
                <strong>
                  ここをクリックするか、
                  <br />
                  PNG/JPG/MOV/MP4をドラッグ＆ドロップして
                  <br />
                  画像登録
                </strong>
                <p class="img-file-error">
                  ※カバー写真を登録する際は画像のアスペクト比を 16:9
                  に近づけてください。比率が大きく異なる場合はカバー写真を選択できません。
                </p>
              </div>
            </section>
          </o-upload>
          <div v-else>
            <v-icon v-if="imageDialog.mediaFile.mediaFormat.name === 'VIDEO'" x-large>
              far fa-file-video
            </v-icon>
            <v-img v-else contain :src="imageDialog.mediaFile.src" />
            <span>{{ imageDialog.mediaFile.file.name }}</span>
            <span v-if="imageDialog.mediaFile.width && imageDialog.mediaFile.height">
              横{{ imageDialog.mediaFile.width }} x 縦{{ imageDialog.mediaFile.height }}
            </span>
          </div>
          <p v-if="imageError !== ''" class="img-file-error">
            {{ imageError }}
          </p>
          <v-select
            v-model="imageDialog.selectedCategory"
            :items="imageDialog.categoryInfos"
            item-title="title"
            item-value="category"
            label="画像のカテゴリ"
            style="width: 80%"
          />
          <div class="store-selector">
            <auto-complete-card
              v-model="imageDialog.selectedStores"
              :items="imageDialog.storeList"
              :disabled="allSelect"
              label="追加する店舗"
              unit="店舗"
              variant="filled"
              style="width: 80%"
            />
            <v-checkbox v-model="allSelect" class="all-select" hide-details color="primary">
              <template #label>
                <span class="text-black">全ての店舗</span>
              </template>
            </v-checkbox>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="imageDialog.show = false">キャンセル</v-btn>
          <v-btn
            color="primary"
            :disabled="
              imageError !== '' ||
              !imageDialog.selectedCategory ||
              (allSelect === false && imageDialog.selectedStores.length === 0)
            "
            @click="addImage"
          >
            追加
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- 反映中のダイアログ (プログレスバーつき) -->
    <v-dialog v-model="applyDialog.show" width="500" persistent>
      <v-card class="reflection-dialog">
        <v-card-title class="headline grey lighten-2" primary-title>
          {{ applyDialog.title }}
        </v-card-title>
        <v-progress-linear v-model="applyDialog.percentage" color="light-blue"></v-progress-linear>
        <v-card-text>
          <div>{{ applyDialog.message }}</div>
          <ul
            v-if="applyDialog.errorMessages && applyDialog.errorMessages.length > 0"
            class="error-dialog"
          >
            <li v-for="(errorMes, index) in applyDialog.errorMessages" :key="index">
              <p class="error-head">{{ errorMes.message }}</p>
              <dl v-if="errorMes.action" class="error-action">
                <dt>操作</dt>
                <dd>
                  {{ errorMes.action }}
                </dd>
              </dl>
              <dl v-if="errorMes.store" class="error-store">
                <dt>店舗コード / 店舗名</dt>
                <dd>{{ errorMes.store }}</dd>
              </dl>
              <dl v-if="errorMes.status" class="error-status">
                <dt>エラーステータス</dt>
                <dd>{{ errorMes.status }}</dd>
              </dl>
              <dl v-if="errorMes.reasons" class="error-reason">
                <dt>原因</dt>
                <dd v-for="(reason, reasonIndex) in errorMes.reasons" :key="reasonIndex">
                  {{ reason }}
                </dd>
              </dl>
              <ul v-if="errorMes.solutions" class="error-solution">
                <li v-for="(solution, solIndex) in errorMes.solutions" :key="solIndex">
                  {{ solution }}
                </li>
              </ul>
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            :disabled="!applyDialog.button"
            @click="applyDialog.show = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- 全店舗から削除確認ダイアログ -->
    <v-dialog v-model="bulkApplyDeleteDialog.show" width="500" persistent>
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          {{ bulkApplyDeleteDialog.targetColumn?.category === 'PROFILE' ? '全店舗からロゴを削除' : '全店舗から同じ画像を削除' }}
        </v-card-title>
        <v-card-text>
          <p v-if="bulkApplyDeleteDialog.targetColumn?.category === 'PROFILE'">
            全店舗のロゴを削除対象として選択します。
          </p>
          <p v-else>
            この画像と同じ画像を、全店舗の同一カテゴリ内から検索して削除対象として選択します。
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="bulkApplyDeleteDialog.show = false">キャンセル</v-btn>
          <v-btn color="primary" @click="executeBulkApplyDelete">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- 全画像削除確認ダイアログ -->
    <v-dialog v-model="deleteAllImagesDialog.show" width="500" persistent>
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>全画像を削除</v-card-title>
        <v-card-text>
          <p>すべての画像を削除します。</p>
          <p>この変更は元に戻せません。本当によろしいですか？</p>
          <p class="mt-4 mb-2">削除を実行するには、「完全に削除」と入力してください。</p>
          <v-text-field
            v-model="deleteAllImagesDialog.confirmationText"
            placeholder="完全に削除"
            outlined
            dense
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteAllImagesDialog.show = false">キャンセル</v-btn>
          <v-btn
            color="error"
            :disabled="deleteAllImagesDialog.confirmationText !== '完全に削除'"
            @click="deleteAllImages"
          >
            削除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- 変更内容反映確認ダイアログ -->
    <v-dialog v-model="applyConfirmDialog.show" width="500" persistent>
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>変更内容を反映</v-card-title>
        <v-card-text>
          <p>{{ applyConfirmDialog.message }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="applyConfirmDialog.show = false">キャンセル</v-btn>
          <v-btn color="primary" @click="confirmApplyChanges">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- XLSXインポートダイアログ -->
    <xlsx-import-dialog
      v-if="showXlsxImportDialog"
      ref="xlsxImportDialog"
      :files="[xlsxFile]"
      :stores="selectedAreaStores"
      :media-files="mediaFiles"
      :finish="importFileDialogFinish"
      :abort-import="abortImport"
      @get-target-store-data="getTargetStoreData"
    ></xlsx-import-dialog>

    <!-- グループ選択欄 -->
    <div class="d-flex align-center mb-2">
      <auto-complete-card
        v-if="!canShowAllowedStoresOnly"
        v-model="selectedAreaIDs"
        :items="areaItems"
        :disabled="isDirty"
        label="グループを選択して下さい"
        unit="グループ"
        show-all-select
        :chip-count="6"
        hint=""
        :persistent-hint="false"
        hide-details
        single-line
        density="compact"
        :clearable="false"
        data-testid="area-selector"
        class="me-1 w-25"
      />
      <v-btn
        v-if="!canShowAllowedStoresOnly"
        color="primary"
        size="small"
        :disabled="isDirty"
        data-testid="show-groups"
        @click="selectAreas()"
      >
        表示
      </v-btn>
      <v-text-field
        v-model="searchWord"
        :disabled="isNotOperationAllowed"
        label="検索キーワード"
        variant="underlined"
        density="compact"
        single-line
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        color="primary"
        style="width: 200px; max-width: 200px"
        @keypress.enter="selectAreas()"
        @click:clear="
          searchWord = '';
          selectAreas();
        "
      />
      <v-btn
        color="primary"
        class="filter-button mr-2"
        size="small"
        :disabled="isNotOperationAllowed"
        @click="selectAreas()"
      >
        絞り込み
      </v-btn>
      <v-btn
        v-if="canManage"
        class="primary mr-5"
        size="small"
        :disabled="isNotOperationAllowed || !isDirty"
        @click="selectAreas(true)"
      >
        変更内容を破棄
      </v-btn>
      <v-btn
        class="primary"
        size="small"
        :disabled="
          featureToggle.getStatus('companies_x_gmbmedia_histories') === 'STOP' ||
          isInactiveMediaHistories
        "
        tag="router-link"
        :to="{ name: 'GMBMediaHistories' }"
      >
        画像の更新履歴
      </v-btn>
    </div>
    <!-- ボタン類 -->
    <div class="d-flex align-center">
      <v-btn
        v-if="canManage"
        color="success"
        class="mr-2"
        size="small"
        :disabled="isNotOperationAllowed"
        @click="showImageDialog()"
      >
        新しい画像を追加
      </v-btn>
      <v-btn
        v-if="canManage"
        color="error"
        class="mr-2"
        size="small"
        :disabled="isNotOperationAllowed || isLoading > 0"
        @click="showDeleteAllImagesDialog"
      >
        全画像を削除
      </v-btn>
      <v-btn
        v-if="canManage"
        color="info"
        class="mr-5"
        size="small"
        :disabled="isNotOperationAllowed || !isDirty"
        @click="showApplyConfirmDialog"
      >
        変更内容を反映
      </v-btn>
      <input ref="importInput" type="file" hidden @change="importFile" />
      <v-btn
        v-if="canManage"
        class="primary"
        size="small"
        :disabled="isNotOperationAllowed || isLoading > 0"
        @click="($refs.importInput as HTMLInputElement).click()"
      >
        XLSXインポート
      </v-btn>
      <v-btn
        class="primary"
        size="small"
        :disabled="isNotOperationAllowed || isLoading > 0"
        @click="loadBatchOfStores"
      >
        XLSXエクスポート
      </v-btn>
      <ToolTipIcon
        :label="'「XLSXエクスポート」はすぐに出力され、画像の一括追加に使えます。「XLSX全件クスポート」は選択中の全グループ、または検索キーワードで絞り込まれた全ての店舗の画像データを読み込んでXLSXファイルを出力するため時間はかかりますが、画像の一括削除にも使えます。'"
      />
      <span v-if="selectedAreaStores.length > 0" class="ml-4">
        <span v-if="isLoading === 3">
          インポート対象読み込み中
          {{ Math.floor((numOfLoadedStores / targetStores.length) * 100) }}％完了
        </span>
        <span v-else-if="isLoading === 2">
          全件読み込み中
          {{ Math.floor((numOfLoadedStores / selectedAreaStores.length) * 100) }}％完了
        </span>
        <span v-else-if="isLoading === 1">読み込み中</span>
        全{{ selectedAreaStores.length }}店舗
        <span v-if="selectedImageCount > 0" class="ml-2">
          {{ selectedImageCount }}枚の画像を選択中
        </span>
      </span>
    </div>
    <v-pagination
      v-model="page"
      :disabled="isLoading > 0 ? true : false"
      :length="displayedStores.length"
      density="compact"
      size="default"
      class="mb-1"
      @update:model-value="getStorePics('normal')"
    />
    <!-- 画像一覧表 -->
    <div v-if="displayedStores.length !== 0" style="height: calc(100vh - 230px)" class="table">
      <table class="imagetable">
        <thead>
          <tr>
            <th>店舗ID</th>
            <th style="min-width: 105px">店舗コード</th>
            <th>店舗名</th>
            <th colspan="100%">画像</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="store of displayedStores[page - 1]" :key="store.poiId">
            <td>
              {{ store.poiId }}
            </td>
            <td>
              <div>{{ store.storeCode }}</div>
            </td>
            <td>
              <div style="width: 150px">{{ store.storeName }}</div>
            </td>
            <!-- 画像表示セル 読込中 -->
            <td v-if="store.isLoading === true">読み込み中</td>
            <!-- 画像表示セル start -->
            <td v-else-if="store.columns.length === 0">画像が登録されていません</td>
            <td v-else class="image-columns">
              <div
                v-for="column of store.columns"
                :key="column.key"
                :class="
                  column.manipulation === 'ADD_IMAGE'
                    ? column.addCheckbox
                      ? 'dirty'
                      : ''
                    : column.manipulation === 'NONE'
                      ? ''
                      : 'dirty'
                "
                class="image-column-item"
              >
                <div class="image-column-header">
                  <v-select
                    v-if="canManage"
                    v-model="column.category"
                    :items="
                      getCategoryInfo(column.category).canChangeFrom
                        ? column.getCanChangeToCategoryInfos()
                        : [getCategoryInfo(column.category)]
                    "
                    item-title="title"
                    item-value="category"
                    :disabled="!getCategoryInfo(column.category).canChangeFrom"
                    dense
                    solo
                    @update:model-value="changeCategory(column)"
                  />
                  <span v-else>
                    {{ getCategoryInfo(column.category).title }}
                  </span>
                </div>
                <div v-if="canManage" class="image-column-controls">
                  <div class="image-column-first-row">
                    <v-checkbox
                      v-if="
                        column.manipulation !== 'ADD_IMAGE' &&
                        !isLogoReplaceFlow(store, column)
                      "
                      v-model="column.deleteCheckbox"
                      label="削除"
                      hide-details
                      class="delete-checkbox"
                      @change="toggleDelete(store, column)"
                    ></v-checkbox>
                    <v-checkbox
                      v-else-if="column.manipulation === 'ADD_IMAGE'"
                      v-model="column.addCheckbox"
                      label="追加"
                      hide-details
                      class="add-checkbox"
                      @update:model-value="(v) => onAddCheckboxChange(store, column, v)"
                    ></v-checkbox>
                    <span
                      v-else-if="isLogoReplaceFlow(store, column)"
                      class="image-column-replace-placeholder"
                      aria-hidden="true"
                    >&nbsp;</span>
                  </div>
                  <div class="image-column-message-row">
                    <p
                      v-if="isNewLogoInReplaceFlow(store, column)"
                      class="text-caption mb-0"
                      style="color: #c62828;"
                    >
                      ロゴは1枚のみのため、追加すると現在掲載中のロゴは自動で削除されます。取り消すにはこのチェックを外してください。
                    </p>
                    <p
                      v-else-if="isLogoReplaceFlow(store, column)"
                      class="text-caption mb-0 mt-0"
                      style="color: #c62828;"
                    >
                      置き換えで削除されます。取り消すには、同じ店舗の新しいロゴの「追加」チェックを外してください。
                    </p>
                  </div>
                  <v-btn
                    v-if="
                      column.manipulation === 'DELETE' &&
                      column.deleteCheckbox &&
                      column.mediaItem &&
                      column.src &&
                      !isBulkSelected(store, column) &&
                      !isLogoReplaceFlow(store, column)
                    "
                    small
                    :disabled="isBulkSelecting"
                    class="bulk-apply-btn bulk-apply-btn-green"
                    @click="showBulkApplyDeleteDialog(store, column)"
                  >
                    {{ column.category === 'PROFILE' ? '全店舗からロゴを削除' : '全店舗から同じ画像を削除' }}
                  </v-btn>
                  <v-btn
                    v-if="
                      column.manipulation === 'DELETE' &&
                      column.deleteCheckbox &&
                      column.mediaItem &&
                      column.src &&
                      isBulkSelected(store, column) &&
                      !isLogoReplaceFlow(store, column)
                    "
                    small
                    :disabled="isBulkSelecting"
                    class="bulk-apply-btn bulk-apply-btn-red"
                    @click="clearBulkSelectedImages(store, column)"
                  >
                    {{ column.category === 'PROFILE' ? '全店舗のロゴ選択を解除' : '同じ画像の選択を全て解除' }}
                  </v-btn>
                </div>
                <div class="image-column-thumbnail">
                  <v-icon
                    v-if="column.mediaFile && column.mediaFile.mediaFormat.name === 'VIDEO'"
                    x-large
                  >
                    far fa-file-video
                  </v-icon>
                  <v-img
                    v-if="column.src"
                    contain
                    max-height="200"
                    max-width="300"
                    :src="column.src"
                  />
                </div>
              </div>
            </td>
            <!-- 画像表示セル end -->
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axios from "axios";
import type { AxiosError } from "axios";
import pLimit from "p-limit";
import { requiredAuth } from "@/helpers";
import type {
  EntitiesGbpResponseMybusinessListMediaItemsResponse,
  EntitiesStore,
  EntitiesStoresResponse,
  MybusinessListMediaItemsResponse,
  MybusinessMediaItem,
  StorageGMBMediaPostResponse,
} from "@/types/ls-api";
type EntitiesStores = EntitiesStore[];
import { CategoryInfo, MediaFile, allowedExtensions } from "./MediaColumn";
import { type FeatureToggle } from "@/routes/FeatureToggle";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import wordDictionary from "@/word-dictionary";
import dayjs from "dayjs";
import XlsxImportDialog from "./XlsxImportDialog.vue";
import { getOperationLogParams } from "@/routes/operation-log";
import { TOAST_CRITICAL_DURATION } from "@/const";
type MediaItem = MybusinessMediaItem;
export type Manipulation = "NONE" | "CHANGE_CATEGORY" | "ADD_IMAGE" | "DELETE";
import { useSnackbar } from "@/storepinia/snackbar";
import { useIndexedDb } from "@/storepinia/idxdb";
import { makeSelectItems, type SelectItem } from "@/helpers/select-items";
import AutoCompleteCard from "@/components/shared/auto-complete-card/AutoCompleteCard.vue";
import { parseGMBErrorDetails, getErrorMessage, retry } from "./error";
import { type GmbErrorDetails, GbpResponseError } from "./error";
import {
  api,
  putMediaItemBulkWithRetry,
  deleteMediaItemBulkWithRetry,
  type MediaItemRequest,
  type DeleteMediaItemRequest,
} from "@/helpers/api/gmb-media";
import { useRoute, useRouter } from "vue-router";

/** GBPErrorか否かとステータスコード */
type ErrorWithGBP = {
  errorCode: number;
  isGBPError: boolean;
};

// 反映エラーダイアログに出す内容
type ReflectionErrorMessage = {
  message: string;
  action: string;
  status: string;
  reasons?: string[];
  store: string;
  solutions?: string[];
};

type LoadedStore = {
  loaded: boolean;
  updated: boolean;
  loadedMediaItems?: MediaColumn[];
};
type LoadedStores = {
  [key: string]: LoadedStore;
};
type OneErrorMessage = {
  isDiscontinued: boolean;
  reflectionErrorMessage: ReflectionErrorMessage;
};

type MediaItemsArr = {
  result: MybusinessListMediaItemsResponse;
  oneStoreData: StoreWithImages;
};

type BulkSelectedImage = {
  identifier: string;
  category: string;
};

// 並列処理の定数
const STORE_CONCURRENCY = 20; // 店舗の並列処理数
const MEDIA_LOAD_CONCURRENCY = 20; // 画像データ読み込みの並列数

type PickingMode = "all" | "import" | "normal";

export class MediaColumn {
  static counter: number = 0;
  key: number;
  manipulation: Manipulation;
  category: string;
  deleteCheckbox: boolean = false;
  /** ADD_IMAGEのとき追加対象であることを示す。チェックを外すと追加対象から外す */
  addCheckbox: boolean = false;
  src: string;
  mediaFile: MediaFile | null;
  mediaItem: MediaItem | null;
  static ofMediaFile(mf: MediaFile, category: string): MediaColumn {
    const ci = CategoryInfo.of(category);
    if (!ci) {
      return null;
    }
    const mc = new MediaColumn();
    mc.key = MediaColumn.counter++;
    mc.manipulation = "ADD_IMAGE";
    mc.category = category;
    mc.addCheckbox = true;
    mc.src = mf.src;
    mc.mediaFile = mf;
    return mc;
  }
  static ofMediaItem(mi: MediaItem): MediaColumn {
    const ci = CategoryInfo.of(mi.locationAssociation.category);
    if (!ci) {
      return null;
    }
    const mc = new MediaColumn();
    mc.key = MediaColumn.counter++;
    mc.manipulation = "NONE";
    mc.category = ci.category;
    // PROFILEの場合サムネイルが404になるので変わりにgoogleUrlを用いる
    mc.src = mc.category == "PROFILE" ? mi.googleUrl : mi.thumbnailUrl;
    mc.mediaItem = mi;
    return mc;
  }
  getCanChangeToCategoryInfos(): CategoryInfo[] {
    const initialValue = this.getInitialCategory();
    if (initialValue) {
      const ini = CategoryInfo.of(initialValue);
      if (!ini.canChangeFrom) {
        return [ini];
      }
    }
    return CategoryInfo.values.filter((ci) => ci.canChangeTo || initialValue === ci.category);
  }
  getInitialCategory(): string {
    return this.mediaItem?.locationAssociation?.category ?? this.category;
  }
  applyChangeCategory(): void {
    const categoryInfo = CategoryInfo.of(this.category);
    if (this.mediaItem) {
      const initialCategory = this.getInitialCategory();
      if (initialCategory === categoryInfo.category) {
        this.manipulation = "NONE";
      } else {
        this.manipulation = "CHANGE_CATEGORY";
      }
    }
    this.deleteCheckbox = false;
  }
  applyDeleteCheckbox(): void {
    if (this.deleteCheckbox) {
      this.manipulation = "DELETE";
    } else {
      this.applyChangeCategory();
    }
  }
  getThumbnail(): string {
    if (this.mediaFile) {
      return this.mediaFile.file.name;
    }
    return this.mediaItem.thumbnailUrl;
  }
}
export class StoreWithImages {
  poiId: number;
  storeCode: string;
  storeName: string;
  gmbName: string;
  columns: MediaColumn[];
  isLoading: boolean;
  static of(store: EntitiesStore): StoreWithImages {
    const s = new StoreWithImages();
    s.poiId = store.poiID;
    s.storeCode = store.gmbStoreCode;
    s.storeName = store.name;
    s.gmbName = store.gmbLocationID;
    s.columns = [];
    s.isLoading = null;
    return s;
  }

  toString(): string {
    return this.storeName;
  }
}

// コンポーネント定義
export default defineComponent({
  components: { XlsxImportDialog, AutoCompleteCard },
  props: {
    poiGroupId: { type: String, required: true, default: "0" },
  },
  data: () => {
    return {
      $route: useRoute(),
      $router: useRouter(),
      featureToggle: {} as FeatureToggle,
      imageDialog: {
        show: false,
        mediaFile: null as MediaFile,
        storeList: [] as SelectItem[], // 店舗一覧(オートコンプリート用、重複あり)
        selectedStores: [] as number[],
        categoryInfos: [] as CategoryInfo[], // カテゴリプルダウン用
        selectedCategory: null as string,
      },
      applyDialog: {
        show: false,
        percentage: 0,
        message: "",
        errorMessages: [] as ReflectionErrorMessage[],
        button: false,
        title: "変更内容を反映中",
      },
      isDirty: false,
      areaItems: [] as SelectItem[],
      getCategoryInfo: CategoryInfo.of,
      stores: [] as EntitiesStores,
      selectedAreaIDs: [] as number[],
      mediaFiles: [] as MediaFile[],
      selectedAreaStores: [] as StoreWithImages[], // 店舗一覧(テーブル用、重複なし)
      isBulkSelecting: false, // 一括選択処理中フラグ
      bulkApplyDeleteDialog: {
        show: false,
        targetStore: null as StoreWithImages | null,
        targetColumn: null as MediaColumn | null,
      },
      deleteAllImagesDialog: {
        show: false,
        confirmationText: "",
      },
      applyConfirmDialog: {
        show: false,
        message: "",
      },
      // 一括選択した画像の情報（解除用）- 複数の画像を保持できるように配列で管理
      bulkSelectedImages: [] as BulkSelectedImage[],
      // 識別子計算のキャッシュ（identifierKey -> identifier）
      identifierCache: {} as { [key: string]: string },
      locationMediaCache: {} as { [name: string]: MediaItem[] },
      showXlsxImportDialog: false,
      xlsxFile: null as File,
      isLoading: 0,
      // 現在のページ
      page: 1,
      // 1ページに何件表示するか
      pageSize: 10,
      // ページ毎に分割した店舗一覧データ
      displayedStores: [] as StoreWithImages[][],
      // 店舗毎読み込み済みフラグ
      loadedStores: {} as LoadedStores,
      // 読み込み済み店舗数
      numOfLoadedStores: 0,
      allLoadTImer: null as NodeJS.Timeout,
      // vueインスタンスが破棄される際に店舗画像中断させる為のフラグ
      isDestroyed: false,
      // 未反映件数
      uploadProgression: 0,
      // 画像追加の全店舗選択チェックボックス
      allSelect: false,
      searchWord: "",
      // 追加しようとした画像が壊れているか、またはファイルサイズが上限下限を超えている場合のエラーメッセージ
      imageError: "",
      // XLSXインポート対象のストア数
      targetStores: [] as StoreWithImages[],
      // 店舗権限ユーザかどうか
      canShowAllowedStoresOnly: useIndexedDb().canShowAllowedStoresOnly,
      // 履歴画面制御
      isInactiveMediaHistories: useIndexedDb().isInactiveMediaHistories,
      // 操作権限があるかどうか
      canManage: useIndexedDb().canManageMedia,
    };
  },
  computed: {
    selectedStoresCount(): number {
      return Array.from(new Set(this.imageDialog.selectedStores)).length;
    },
    isNotOperationAllowed(): boolean {
      if (this.canShowAllowedStoresOnly) {
        return this.displayedStores?.length === 0;
      }
      return this.selectedAreaIDs.length === 0;
    },
    allowedExtensions(): string {
      return allowedExtensions;
    },
    /**
     * 選択中の画像数をカウント
     */
    selectedImageCount(): number {
      let count = 0;
      for (const store of this.selectedAreaStores) {
        for (const column of store.columns) {
          if (column.manipulation === "ADD_IMAGE") {
            if (column.addCheckbox) count++;
          } else if (column.manipulation !== "NONE" || column.deleteCheckbox) {
            count++;
          }
        }
      }
      return count;
    },
    /**
     * 全画像枚数（ADD_IMAGE以外の全カテゴリの画像）
     */
    totalImageCount(): number {
      let count = 0;
      for (const store of this.selectedAreaStores) {
        for (const column of store.columns) {
          if (column.manipulation !== "ADD_IMAGE" && column.mediaItem?.thumbnailUrl) {
            count++;
          }
        }
      }
      return count;
    },
  },
  beforeUnmount: function () {
    this.isDestroyed = true;
    // XLSX全件ダウンロードチェックタイマー止める
    clearInterval(this.allLoadTImer);
  },
  async created() {
    this.featureToggle = this.$route.meta.featureToggle as FeatureToggle;
    // グループプルダウン作成
    ({ areas: this.areaItems } = makeSelectItems(
      useIndexedDb().areaStores,
      useIndexedDb().stores?.stores,
      false
    ));
    // 店舗一覧取得
    // Vuexに既にstores保持していたらそちらを使う
    let storeData: EntitiesStore[];
    if (useIndexedDb().stores?.stores) {
      storeData = useIndexedDb().stores.stores;
    } else {
      const storesres = await requiredAuth<EntitiesStoresResponse>(
        "get",
        `${import.meta.env.VITE_APP_API_BASE}v1/companies/${this.poiGroupId}/stores`
      );
      storeData = storesres.data.stores;
    }
    await api.gmbapiinit(Number(this.poiGroupId)); // GBPアクセストークンの初期化
    this.stores = (storeData ?? []).filter((s) => s.enabled);
    // 店舗権限の場合は全店舗を初期表示する
    if (this.canShowAllowedStoresOnly) {
      this.selectAreas();
    }
  },
  methods: {
    /** 画像追加ダイアログの店舗プルダウンアイテムを作成 */
    updateImageDialogStoreList(): void {
      if (this.searchWord || this.canShowAllowedStoresOnly) {
        this.imageDialog.storeList = this.selectedAreaStores.map((s) => {
          return { isHeader: false, id: s.poiId, title: s.storeName };
        });
      } else {
        const areas = useIndexedDb().areaStores.filter((a) =>
          this.selectedAreaIDs.includes(a.areaID)
        );
        ({ stores: this.imageDialog.storeList } = makeSelectItems(areas, this.stores, false));
        if (this.imageDialog.storeList.filter((s) => s.isHeader).length === 1) {
          this.imageDialog.storeList = this.imageDialog.storeList.filter((s) => !s.isHeader);
        }
      }
    },
    async selectAreas(destruction: boolean = false) {
      if (this.selectedAreaIDs.length === 0 && !this.canShowAllowedStoresOnly) {
        // グループ指定しないで「表示」クリックするとそのままではSentryエラーが出るのを抑止
        return;
      }
      this.imageDialog.storeList = [];
      this.isDirty = false;
      const displayedStores: StoreWithImages[] = [];
      // ページリセットする
      this.page = 1;
      if (destruction === true) {
        // 変更内容を破棄：現在表示されているcolumnsの状態をリセット
        for (const store of this.selectedAreaStores) {
          store.columns = store.columns.filter((col) => col.manipulation !== "ADD_IMAGE");
          for (const col of store.columns) {
            col.manipulation = "NONE";
            col.deleteCheckbox = false;
          }
        }
        // キャッシュの変更内容もリセット
        Object.keys(this.loadedStores).forEach((label: string): void => {
          if (this.loadedStores[label].loadedMediaItems) {
            for (const mediaColumn of this.loadedStores[label].loadedMediaItems) {
              mediaColumn.manipulation = "NONE";
              mediaColumn.deleteCheckbox = false;
            }
          }
        });
        this.bulkSelectedImages = [];
        // 画像追加ダイアログの店舗リストを作成
        this.updateImageDialogStoreList();
        return;
      } else {
        // グループ変更の場合
        this.selectedAreaStores = [];
        Object.keys(this.loadedStores).forEach((label: string): void => {
          this.loadedStores[label].loaded = false;
        });
      }

      for (const store of this.stores) {
        const canUse =
          this.selectedAreaIDs.filter((id) => (store.areas ?? []).includes(id)).length !== 0;
        // 店舗参照権限ではエリア参照できないので全表示のみ、それ以外はエリアに紐付く店舗のみ
        if (canUse || this.canShowAllowedStoresOnly) {
          const storeWithImage: StoreWithImages = StoreWithImages.of(store);
          if (this.checkStoreMatching(storeWithImage) === true) {
            this.selectedAreaStores.push(storeWithImage);
            displayedStores.push(storeWithImage);
            if (!this.loadedStores[store.poiID]) {
              this.loadedStores[store.poiID] = { loaded: false, updated: false };
            }
          }
        }
      }
      this.updateImageDialogStoreList();
      if (displayedStores.length > 0) {
        // pageSize単位でストア格納する
        this.displayedStores = this.divide(displayedStores, this.pageSize);
        await this.getStorePics("normal");
      }
    },

    /** 店舗毎に accounts.locations.media.list を実行して、サムネイルを表示していく */
    async getStorePics(mode: PickingMode, importTargets?: StoreWithImages[][]): Promise<void> {
      let storeData: StoreWithImages[];
      this.numOfLoadedStores = 0;
      if (mode === "all") {
        // XLSX全件エクスポートクリックの場合
        this.isLoading = 2;
        for (storeData of this.displayedStores) {
          await this.pickStores(storeData, mode);
        }
      } else if (mode === "import") {
        // XLSXインポートで必要な分を抜粋の場合
        this.isLoading = 3;
        for (storeData of importTargets) {
          await this.pickStores(storeData, mode);
        }
      } else {
        // 通常の店舗データロード
        this.isLoading = 1;
        storeData = this.displayedStores[this.page - 1];
        await this.pickStores(storeData, mode);
      }
    },
    /** データロードする必要がある店舗の選別をする */
    async pickStores(storeData: StoreWithImages[], mode: PickingMode): Promise<void> {
      try {
        // 一旦ページ内全部の店舗を「読み込み中」にする
        for (const s of storeData) {
          s.isLoading = true;
        }
        const tasks: (() => Promise<MediaItemsArr>)[] = [];
        for (const s of storeData) {
          const loadedOne = this.loadedStores[s.poiId];
          if (loadedOne.loaded === true) {
            // ロード済みでグループ変更もされてないならisLoadingフラグを下ろすだけで何もしない
            s.isLoading = false;
          } else if (loadedOne.loadedMediaItems) {
            // 別のグループ選択するも既にキャッシュ済みならそれを使う
            s.columns = loadedOne.loadedMediaItems;
            s.isLoading = false;
            this.loadedStores[s.poiId].loaded = true;
            this.numOfLoadedStores++;
          } else {
            if (loadedOne.updated === true) {
              // 画像アップロード反映後ページ遷移して戻ってきたときに重複しない様にカラムのをリセットする
              s.columns.length = 0;
            }
            // キャッシュが無いものは普通にAPI叩いて取りに行く
            tasks.push(() => this.getMediaItems(s, ""));
          }
        }
        await this.getPromisedItems(mode, tasks);
      } catch (error) {
        console.log("[pickStores ERROR]", error);
      }
    },

    /** listMediaItem の実行と、そこで発生したエラーの処理をまとめた関数 */
    async getMediaItems(store: StoreWithImages, pageToken: string): Promise<MediaItemsArr> {
      let response: EntitiesGbpResponseMybusinessListMediaItemsResponse = null;
      try {
        response = await retry(() =>
          api.listMediaItem(Number(this.poiGroupId), store.poiId, 2500, pageToken)
        );
      } catch (e: any) {
        console.error("[getMediaItems] ERROR", e);
        if (axios.isAxiosError(e)) {
          throw e; // AxiosError はそのまま throw する
        }
        response = (e as GbpResponseError).res; // GbpResponseError に含まれている GbpResponse を取り出す
      }
      const statusCode = response.statusCode;
      if (statusCode === 200) {
        return { result: response.data, oneStoreData: store };
      }
      const errorText = getGbpErrorMessage(statusCode);
      useSnackbar().addSnackbarMessages({
        text: errorText,
        color: "danger",
        timeout: TOAST_CRITICAL_DURATION,
      });
      return null;
    },
    /** 1ページ分の店舗画像MediaItem取得し終わった際の処理 */
    async getPromisedItems(
      mode: PickingMode,
      tasks: (() => Promise<MediaItemsArr>)[]
    ): Promise<void> {
      try {
        // 同時実行数を 5 に制限しつつ、すべての店舗のMediaItem取得を実行する
        const limit = pLimit(5);
        const ps = tasks.map((fn) => limit(fn));
        const messages = await Promise.all(ps);
        if (this.isDestroyed === true) {
          // 店舗画像の一括更新ページから離脱していたら中止する
          return;
        }
        // 読み込み未完了フラグ
        let incomplete = false;
        for (const message of messages) {
          if (!message) continue; // エラーが出ていた場合は null なのでスキップ
          const store: StoreWithImages = message.oneStoreData;
          for (const mediaItem of message.result.mediaItems ?? []) {
            const mc = MediaColumn.ofMediaItem(mediaItem);
            if (!mc) {
              continue;
            }
            // src が重複していなければ、店舗に画像を追加する
            if (!store.columns.some((column) => mc.src === column.src)) {
              store.columns.push(mc);
            }
          }

          // カバーとロゴを先頭に持っていく
          store.columns.sort((a, b) => {
            return CategoryInfo.compare(a.category, b.category);
          });

          const pageToken = message.result.nextPageToken;
          if (pageToken) {
            // 店舗画像続き(nextPageToken)あったらそれを読みに行く
            // pageSize: 2500 設定ならば、実運用上はまずこの分岐は発生しないと思われる

            // 読み込み未完了フラグ立てる
            incomplete = true;
            this.getPromisedItems(mode, [() => this.getMediaItems(store, pageToken)]);
          } else {
            store.isLoading = false;
            // 既に読み込んだ画像をもう一度読み込みに行かない様にフラグ立てる
            this.loadedStores[store.poiId].loaded = true;
            // ロード済み店舗カウントアップ
            this.numOfLoadedStores++;
            this.loadedStores[store.poiId].loadedMediaItems = Object.assign([], store.columns);
          }
        }

        if (incomplete === false && this.isLoading === 1) {
          // 単ページ読み込み(isLoading = 1)ならばここでローディング状態は解除
          this.isLoading = 0;
        }
      } catch (e: any) {
        console.error("[getPromisedItems ERROR]", e);
        useSnackbar().addSnackbarMessages({
          text: "しばらく時間を置いた後、もう一度お試しください。",
          color: "danger",
          timeout: TOAST_CRITICAL_DURATION,
        });
      }
    },

    /** 指定されたサイズごとに配列を分割する */
    divide<T>(array: T[], size: number): T[][] {
      if (size < 1) {
        size = 1;
      }
      const dividedArray: T[][] = [];
      while (array.length) {
        dividedArray.push(array.splice(0, size));
      }
      return dividedArray;
    },
    showImageDialog() {
      this.imageError = "";
      this.imageDialog.mediaFile = null;
      this.imageDialog.categoryInfos = [];
      this.imageDialog.selectedCategory = null;
      this.imageDialog.selectedStores = [];
      this.imageDialog.show = true;
    },
    async loadFile(file: File) {
      this.imageError = "";
      // 新しいファイルならばメディアファイル一覧に追加する
      const newmf = await MediaFile.of(file);
      if (newmf.imageError !== "") {
        // 画像破損だったらエラー表示出す
        this.imageError = newmf.imageError;
        return;
      }
      let mf = this.mediaFiles.find((mf) => mf.equals(newmf));
      if (!mf) {
        this.mediaFiles.push(newmf);
        mf = newmf;
      }
      this.imageDialog.mediaFile = mf;
      this.imageDialog.categoryInfos = mf.getMakeableCategoryInfos();
    },
    async addImage() {
      this.imageDialog.show = false;
      // すべての店舗のチェックがついていた場合は、全店舗のidを用いる
      const selectedStores = this.allSelect
        ? this.selectedAreaStores.map((s) => s.poiId)
        : this.imageDialog.selectedStores;

      const ci = CategoryInfo.of(this.imageDialog.selectedCategory);
      // 重複不可カテゴリ（PROFILE等）は既存列を削除対象にする必要がある。未表示ページの店舗は
      // columns が空のままなので、先に全対象店舗のメディアを読み込む。
      const needsFullMediaLoad =
        ci &&
        !ci.canDuplicate &&
        selectedStores.some((id) => {
          const loadedOne = this.loadedStores[id];
          return !loadedOne || !loadedOne.loaded || !loadedOne.loadedMediaItems;
        });

      if (needsFullMediaLoad) {
        this.applyDialog.show = true;
        this.applyDialog.percentage = 0;
        this.applyDialog.title = "画像を追加";
        this.applyDialog.button = false;
        this.applyDialog.errorMessages = [];
        await this.$nextTick();
        try {
          // loadStoreMediaDataIfNeeded は未読込店舗の columns を API 結果で置き換えるため、
          // 未反映の ADD_IMAGE（他カテゴリの追加など）が消えないよう先に退避する
          const pendingAddImagesMap = new Map<number, MediaColumn[]>();
          for (const s of this.selectedAreaStores) {
            const pending = s.columns.filter((col) => col.manipulation === "ADD_IMAGE");
            if (pending.length > 0) {
              pendingAddImagesMap.set(s.poiId, [...pending]);
            }
          }
          await this.loadStoreMediaDataIfNeeded(
            ci.category === "PROFILE"
              ? "ロゴ置換のため全店舗の画像を読み込み中..."
              : "全店舗の画像を読み込み中..."
          );
          for (const s of this.selectedAreaStores) {
            const pending = pendingAddImagesMap.get(s.poiId);
            if (pending && pending.length > 0) {
              const existingKeys = new Set(
                s.columns.filter((col) => col.manipulation === "ADD_IMAGE").map((col) => col.key)
              );
              const toRestore = pending.filter((img) => !existingKeys.has(img.key));
              if (toRestore.length > 0) {
                s.columns.unshift(...toRestore);
              }
            }
            s.columns.sort((a, b) => CategoryInfo.compare(a.category, b.category));
          }
        } finally {
          this.applyDialog.show = false;
          await this.$nextTick();
        }
      }

      for (const id of selectedStores) {
        const store = this.selectedAreaStores.find((s) => s.poiId === id);
        const newCol = MediaColumn.ofMediaFile(this.imageDialog.mediaFile, ci.category);
        if (ci.canDuplicate) {
          store.columns.unshift(newCol);
        } else {
          for (let i = store.columns.length - 1; 0 <= i; i--) {
            const col = store.columns[i];
            if (col.category === ci.category) {
              col.deleteCheckbox = true;
              this.toggleDelete(store, col);
            }
          }
          store.columns.unshift(newCol);
        }

        store.columns.sort((a, b) => {
          return CategoryInfo.compare(a.category, b.category);
        });
        if (this.loadedStores[store.poiId]?.loadedMediaItems) {
          // 正しく追加を反映させる為に、一度読み込んだフラグ(loaded)は立たせつつ、キャッシュ削除しておく
          delete this.loadedStores[store.poiId].loadedMediaItems;
        }
        this.loadedStores[store.poiId].updated = false;
      }
      this.updateIsDirty();
    },
    storeCheckBoxIcon() {
      if (this.imageDialog.selectedStores.length === this.selectedAreaStores.length)
        return "mdi-close-box";
      if (this.imageDialog.selectedStores.length > 0) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    filterAreaStores: (item: StoreWithImages, queryText: string, itemText: string) => {
      const storeName = item?.storeName?.toLocaleLowerCase();
      const searchText = queryText?.toLocaleLowerCase();
      return storeName?.includes(searchText);
    },
    toggleDelete(s: StoreWithImages, c: MediaColumn) {
      if (c.manipulation === "ADD_IMAGE") {
        // 追加チェックを外す：行は残し、追加対象から外す（背景は白に戻る）
        c.addCheckbox = false;
      } else {
        // 既存の画像であればカラムに変更を適用する
        c.applyDeleteCheckbox();
        // 変更されたのが重複できないタイプで、削除フラグをOFFにする操作ならば、その他の同じタイプのものを削除する
        // （追加予定の列 ADD_IMAGE は残す。現在のロゴの削除を外しても新しいロゴの行は残す）
        if (CategoryInfo.of(c.category).canDuplicate === false && c.deleteCheckbox === false) {
          for (let i = s.columns.length - 1; 0 <= i; i--) {
            const col = s.columns[i];
            if (
              col.key !== c.key &&
              col.category === c.category &&
              col.manipulation !== "ADD_IMAGE"
            ) {
              s.columns.splice(i, 1);
            }
          }
        }
      }
      this.updateIsDirty();
    },
    changeCategory(c: MediaColumn) {
      c.applyChangeCategory();
      this.updateIsDirty();
    },
    /**
     * 画像の識別子を生成する（キャッシュ付き）
     * createTime, dimensions, sourceUrl（クエリパラメータ除去）, categoryを組み合わせて識別子を生成
     */
    getImageIdentifier(mediaItem: MediaItem, category: string): string {
      if (!mediaItem) {
        return "";
      }

      const heightPixels = String(mediaItem.dimensions?.heightPixels || 0);
      const widthPixels = String(mediaItem.dimensions?.widthPixels || 0);
      const sourceUrl = mediaItem.sourceUrl || "";

      // 識別子を生成（createTimeは除外：アップロードタイミングで異なる可能性があるため）
      const identifierKey = `${heightPixels}_${widthPixels}_${sourceUrl}_${category}`;

      // キャッシュをチェック
      if (this.identifierCache[identifierKey]) {
        return this.identifierCache[identifierKey];
      }

      // 簡易的なハッシュ関数（同期的に計算）
      let hash = 0;
      for (let i = 0; i < identifierKey.length; i++) {
        const char = identifierKey.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      const hashStr = Math.abs(hash).toString(16).padStart(8, "0");
      this.identifierCache[identifierKey] = hashStr;
      return hashStr;
    },
    async loadStoreMediaDataIfNeeded(progressMessage: string): Promise<void> {
      const storesToLoad: StoreWithImages[] = [];
      let alreadyLoadedCount = 0;
      const totalStores = this.selectedAreaStores.length;

      // 画像データが読み込まれていない店舗をリストアップ
      for (const s of this.selectedAreaStores) {
        const loadedOne = this.loadedStores[s.poiId];
        if (!loadedOne || !loadedOne.loaded || !loadedOne.loadedMediaItems) {
          storesToLoad.push(s);
        } else if (loadedOne.loadedMediaItems) {
          s.columns = loadedOne.loadedMediaItems;
          alreadyLoadedCount++;
        }
      }

      if (storesToLoad.length === 0) {
        return;
      }

      this.applyDialog.message = `${progressMessage} (${alreadyLoadedCount}/${totalStores}店舗)`;
      await this.$nextTick();

      const loadTasks: (() => Promise<MediaItemsArr>)[] = [];
      for (const s of storesToLoad) {
        if (!this.loadedStores[s.poiId]) {
          this.loadedStores[s.poiId] = { loaded: false, updated: false };
        }
        loadTasks.push(() => this.getMediaItems(s, ""));
      }

      const limit = pLimit(MEDIA_LOAD_CONCURRENCY);
      const loadPromises = loadTasks.map((fn) => limit(fn));

      let loadedCount = 0;
      const updateProgress = async () => {
        const currentLoaded = alreadyLoadedCount + loadedCount;
        this.applyDialog.percentage = (currentLoaded / totalStores) * 100;
        this.applyDialog.message = `${progressMessage} (${currentLoaded}/${totalStores}店舗)`;
        await this.$nextTick();
      };

      for (let i = 0; i < loadPromises.length; i++) {
        loadPromises[i]
          .then(async (result) => {
            if (result) {
              const store = storesToLoad[i];
              // 既存のcolumnsをクリア
              store.columns = [];
              // 取得した画像データを設定
              for (const mediaItem of result.result.mediaItems ?? []) {
                const mc = MediaColumn.ofMediaItem(mediaItem);
                if (mc && !store.columns.some((column) => mc.src === column.src)) {
                  store.columns.push(mc);
                }
              }
              store.columns.sort((a, b) => CategoryInfo.compare(a.category, b.category));
              this.loadedStores[store.poiId].loaded = true;
              this.loadedStores[store.poiId].loadedMediaItems = Object.assign([], store.columns);
            }
            loadedCount++;
            await updateProgress();
          })
          .catch(async (error) => {
            console.error(`店舗${storesToLoad[i].poiId}の画像データ取得に失敗:`, error);
            loadedCount++;
            await updateProgress();
          });
      }

      await Promise.all(loadPromises);
    },
    /**
     * この画像が一括選択された画像かどうかを判定
     */
    isBulkSelected(store: StoreWithImages, column: MediaColumn): boolean {
      if (this.bulkSelectedImages.length === 0 || !column.deleteCheckbox) {
        return false;
      }
      if (!column.mediaItem) {
        return false;
      }
      // PROFILEの一括選択時は識別子が店舗ごとに異なるため、categoryのみで判定
      if (column.category === "PROFILE") {
        return this.bulkSelectedImages.some(
          (item) => item.category === "PROFILE" && item.identifier === "PROFILE"
        );
      }
      const identifier = this.getImageIdentifier(column.mediaItem, column.category);
      if (!identifier) {
        return false;
      }
      return this.bulkSelectedImages.some(
        (item) => item.category === column.category && item.identifier === identifier
      );
    },
    /**
     * ロゴの「置き換え」状態か（この列が古いロゴで、同じ店舗に追加チェック付きのPROFILE列がある）
     */
    isLogoReplaceFlow(store: StoreWithImages, column: MediaColumn): boolean {
      if (column.category !== "PROFILE" || !column.deleteCheckbox) {
        return false;
      }
      return store.columns.some(
        (col) =>
          col.key !== column.key &&
          col.category === "PROFILE" &&
          col.manipulation === "ADD_IMAGE" &&
          col.addCheckbox
      );
    },
    /**
     * ロゴの「置き換え」状態で、この列が新しいロゴ（追加チェック付き）側か
     */
    isNewLogoInReplaceFlow(store: StoreWithImages, column: MediaColumn): boolean {
      return (
        column.category === "PROFILE" &&
        column.manipulation === "ADD_IMAGE" &&
        column.addCheckbox &&
        store.columns.some(
          (col) =>
            col.key !== column.key &&
            col.category === "PROFILE" &&
            col.deleteCheckbox
        )
      );
    },
    /**
     * 追加チェックを外したときに、同じ店舗の古いロゴの削除選択を連動解除する（PROFILEのみ）
     */
    clearLinkedLogoDelete(store: StoreWithImages, addColumn: MediaColumn): void {
      if (addColumn.category !== "PROFILE") return;
      const targetStore = this.selectedAreaStores.find((s) => s.poiId === store.poiId);
      if (!targetStore) return;
      const oldCol = targetStore.columns.find(
        (col) =>
          col.category === "PROFILE" &&
          col.manipulation === "DELETE" &&
          col.deleteCheckbox
      );
      if (oldCol) {
        oldCol.deleteCheckbox = false;
        oldCol.applyChangeCategory();
        // 一括選択状態を解除し、後から手動で削除を入れても赤ボタンが出ないようにする（赤を押すと他店舗の解除が連動するのを防ぐ）
        const idx = this.bulkSelectedImages.findIndex(
          (item) => item.category === "PROFILE" && item.identifier === "PROFILE"
        );
        if (idx !== -1) this.bulkSelectedImages.splice(idx, 1);
        this.updateIsDirty();
      }
    },
    /**
     * 追加チェックの変更時（PROFILEでは古いロゴの削除と連動）
     */
    onAddCheckboxChange(store: StoreWithImages, column: MediaColumn, checked: boolean): void {
      if (!checked) {
        this.toggleDelete(store, column);
        if (column.category === "PROFILE") {
          this.clearLinkedLogoDelete(store, column);
        }
      } else {
        if (column.category === "PROFILE") this.setLinkedLogoDelete(store, column);
        this.updateIsDirty();
      }
    },
    /**
     * 追加チェックを入れたときに、同じ店舗の古いロゴを削除選択に連動させる（PROFILEのみ）
     */
    setLinkedLogoDelete(store: StoreWithImages, addColumn: MediaColumn): void {
      if (addColumn.category !== "PROFILE") return;
      const targetStore = this.selectedAreaStores.find((s) => s.poiId === store.poiId);
      if (!targetStore) return;
      const oldCol = targetStore.columns.find(
        (col) =>
          col.category === "PROFILE" &&
          col.manipulation !== "ADD_IMAGE"
      );
      if (oldCol) {
        oldCol.deleteCheckbox = true;
        oldCol.applyDeleteCheckbox();
        this.updateIsDirty();
      }
    },
    /**
     * 全店舗に適用確認ダイアログを表示
     */
    showBulkApplyDeleteDialog(store: StoreWithImages, column: MediaColumn) {
      this.bulkApplyDeleteDialog.targetStore = store;
      this.bulkApplyDeleteDialog.targetColumn = column;
      this.bulkApplyDeleteDialog.show = true;
    },
    async executeBulkApplyDelete() {
      this.bulkApplyDeleteDialog.show = false;

      const targetStore = this.bulkApplyDeleteDialog.targetStore;
      const targetColumn = this.bulkApplyDeleteDialog.targetColumn;

      if (!targetStore || !targetColumn || !targetColumn.mediaItem || !targetColumn.src) {
        return;
      }

      this.isBulkSelecting = true;

      const category = targetColumn.category;
      // PROFILEは店舗ごとに1枚でsourceUrlが店舗ごとに異なるため、識別子では一致しない。種別（PROFILE）で全店舗の1枚ずつを選択する
      const isProfileBulk = category === "PROFILE";
      const searchTitle = isProfileBulk ? "ロゴ画像を検索中" : "同じ画像を検索中";
      const searchMessagePrefix = isProfileBulk ? "ロゴ画像を検索中..." : "同じ画像を検索中...";

      try {
        this.applyDialog.show = true;
        this.applyDialog.percentage = 0;
        this.applyDialog.title = searchTitle;
        this.applyDialog.message = searchMessagePrefix;
        this.applyDialog.button = false;
        this.applyDialog.errorMessages = [];
        await this.$nextTick();
        const targetIdentifier = isProfileBulk
          ? "PROFILE"
          : this.getImageIdentifier(targetColumn.mediaItem, category);

        let processedCount = 0;
        let matchedCount = 0;
        const totalStores = this.selectedAreaStores.length;

        // 追加した画像を一時的に保存（loadStoreMediaDataIfNeededで上書きされないように）
        const addedImagesMap = new Map<number, MediaColumn[]>();
        for (const s of this.selectedAreaStores) {
          const addedImages = s.columns.filter((col) => col.manipulation === "ADD_IMAGE");
          if (addedImages.length > 0) {
            addedImagesMap.set(s.poiId, [...addedImages]);
          }
        }

        await this.loadStoreMediaDataIfNeeded("画像データを読み込み中...");

        // 追加した画像を復元（PROFILEの一括削除時は復元しない＝「全店舗からロゴを削除」で新しいロゴの行も消す）
        if (!isProfileBulk) {
          for (const s of this.selectedAreaStores) {
            const addedImages = addedImagesMap.get(s.poiId);
            if (addedImages && addedImages.length > 0) {
              const existingKeys = new Set(
                s.columns.filter((col) => col.manipulation === "ADD_IMAGE").map((col) => col.key)
              );
              const imagesToRestore = addedImages.filter((img) => !existingKeys.has(img.key));
              if (imagesToRestore.length > 0) {
                s.columns.unshift(...imagesToRestore);
              }
            }
          }
        }

        // すべての対象画像の識別子を事前に計算してキャッシュに保存
        this.applyDialog.message = searchMessagePrefix;
        await this.$nextTick();

        const allImagesToCheck: Array<{
          store: StoreWithImages;
          column: MediaColumn;
        }> = [];
        for (const s of this.selectedAreaStores) {
          for (const col of s.columns) {
            if (col.manipulation !== "ADD_IMAGE" && col.category === category && col.mediaItem) {
              allImagesToCheck.push({
                store: s,
                column: col,
              });
            }
          }
        }

        // すべての画像の識別子を計算
        let identifierCalculatedCount = 0;
        for (const { column } of allImagesToCheck) {
          if (column.mediaItem) {
            this.getImageIdentifier(column.mediaItem, category);
            identifierCalculatedCount++;
            if (
              identifierCalculatedCount % 50 === 0 ||
              identifierCalculatedCount === allImagesToCheck.length
            ) {
              this.applyDialog.message = `${searchMessagePrefix} (${identifierCalculatedCount}/${this.totalImageCount}枚の画像を確認中)`;
              await this.$nextTick();
            }
          }
        }

        this.applyDialog.message = `${searchMessagePrefix} (0/${totalStores}店舗)`;
        await this.$nextTick();

        // 各店舗を並列処理
        for (let i = 0; i < this.selectedAreaStores.length; i += STORE_CONCURRENCY) {
          const chunk = this.selectedAreaStores.slice(i, i + STORE_CONCURRENCY);
          await Promise.all(
            chunk.map(async (s) => {
              const imagesToCheck = s.columns.filter(
                (col) =>
                  col.manipulation !== "ADD_IMAGE" && col.category === category && col.mediaItem
              );
              for (const col of imagesToCheck) {
                if (col.mediaItem) {
                  const match = isProfileBulk
                    ? true
                    : this.getImageIdentifier(col.mediaItem, category) === targetIdentifier;
                  if (match) {
                    col.deleteCheckbox = true;
                    this.toggleDelete(s, col);
                    matchedCount++;
                  }
                }
              }

              processedCount++;
              this.applyDialog.percentage = (processedCount / totalStores) * 100;
              this.applyDialog.message = `${searchMessagePrefix} (${processedCount}/${totalStores}店舗、${matchedCount}枚一致)`;
              await this.$nextTick();
            })
          );
        }

        this.applyDialog.percentage = 100;
        this.applyDialog.message = `一括選択が完了しました（${matchedCount}枚の画像を選択）`;
        this.applyDialog.button = true;
        const existingIndex = this.bulkSelectedImages.findIndex(
          (item) => item.identifier === targetIdentifier && item.category === category
        );
        if (existingIndex === -1) {
          this.bulkSelectedImages.push({ identifier: targetIdentifier, category });
        }
        await this.$nextTick();
      } catch (error) {
        console.error("一括選択処理でエラーが発生しました:", error);
        this.applyDialog.message = `一括選択処理でエラーが発生しました: ${
          error instanceof Error ? error.message : String(error)
        }`;
        this.applyDialog.button = true;
        this.applyDialog.errorMessages = [
          {
            message: error instanceof Error ? error.message : String(error),
            action: "一括選択",
            status: "unknown",
            store: "",
          },
        ];
        await this.$nextTick();
      } finally {
        this.isBulkSelecting = false;
      }
    },
    /**
     * 一括選択した画像の選択を全て解除（クリックした画像のみ）
     */
    async clearBulkSelectedImages(store: StoreWithImages, column: MediaColumn) {
      if (!column.mediaItem || !column.src) {
        return;
      }

      const category = column.category;
      if (!column.mediaItem) {
        return;
      }

      // PROFILEの一括選択時は sentinel で登録している
      const targetIdentifier =
        category === "PROFILE" ? "PROFILE" : this.getImageIdentifier(column.mediaItem, category);
      if (!targetIdentifier) {
        return;
      }

      const targetIndex = this.bulkSelectedImages.findIndex(
        (item) => item.identifier === targetIdentifier && item.category === category
      );
      if (targetIndex === -1) {
        return;
      }

      this.isBulkSelecting = true;

      try {
        this.applyDialog.show = true;
        this.applyDialog.percentage = 0;
        this.applyDialog.title = "選択を解除中";
        this.applyDialog.message = "選択を解除中...";
        this.applyDialog.button = false;
        this.applyDialog.errorMessages = [];
        await this.$nextTick();

        let processedCount = 0;
        let clearedCount = 0;
        const totalStores = this.selectedAreaStores.length;

        // 追加した画像を一時的に保存（loadStoreMediaDataIfNeededで上書きされないように）
        const addedImagesMap = new Map<number, MediaColumn[]>();
        for (const s of this.selectedAreaStores) {
          const addedImages = s.columns.filter((col) => col.manipulation === "ADD_IMAGE");
          if (addedImages.length > 0) {
            addedImagesMap.set(s.poiId, [...addedImages]);
          }
        }

        await this.loadStoreMediaDataIfNeeded("画像データを読み込み中...");

        // 追加した画像を復元
        for (const s of this.selectedAreaStores) {
          const addedImages = addedImagesMap.get(s.poiId);
          if (addedImages && addedImages.length > 0) {
            // 既存の追加画像のkeyを取得
            const existingKeys = new Set(
              s.columns.filter((col) => col.manipulation === "ADD_IMAGE").map((col) => col.key)
            );
            // 重複していない追加画像のみを復元
            const imagesToRestore = addedImages.filter((img) => !existingKeys.has(img.key));
            if (imagesToRestore.length > 0) {
              // 追加した画像を先頭に追加
              s.columns.unshift(...imagesToRestore);
            }
          }
        }

        // すべての対象画像の識別子を事前に計算してキャッシュに保存
        this.applyDialog.message = `選択を解除中...`;
        await this.$nextTick();

        const allImagesToCheck: Array<{
          store: StoreWithImages;
          column: MediaColumn;
        }> = [];
        for (const s of this.selectedAreaStores) {
          for (const col of s.columns) {
            if (
              col.manipulation !== "ADD_IMAGE" &&
              col.category === category &&
              col.mediaItem &&
              col.deleteCheckbox
            ) {
              allImagesToCheck.push({
                store: s,
                column: col,
              });
            }
          }
        }

        // すべての画像の識別子を計算
        let identifierCalculatedCount = 0;
        for (const { column: col } of allImagesToCheck) {
          if (col.mediaItem) {
            this.getImageIdentifier(col.mediaItem, category);
            identifierCalculatedCount++;
            if (
              identifierCalculatedCount % 50 === 0 ||
              identifierCalculatedCount === allImagesToCheck.length
            ) {
              this.applyDialog.message = `選択を解除中... (${identifierCalculatedCount}/${this.totalImageCount}枚の画像を確認中)`;
              await this.$nextTick();
            }
          }
        }

        this.applyDialog.message = `選択を解除中... (0/${totalStores}店舗)`;
        await this.$nextTick();

        // 各店舗を並列処理（PROFILEの一括解除時は識別子比較せず category が PROFILE の列をすべて解除）
        const isProfileBulkClear = category === "PROFILE" && targetIdentifier === "PROFILE";
        for (let i = 0; i < this.selectedAreaStores.length; i += STORE_CONCURRENCY) {
          const chunk = this.selectedAreaStores.slice(i, i + STORE_CONCURRENCY);
          await Promise.all(
            chunk.map(async (s) => {
              const imagesToCheck = s.columns.filter(
                (col) =>
                  col.manipulation !== "ADD_IMAGE" &&
                  col.category === category &&
                  col.mediaItem &&
                  col.deleteCheckbox
              );
              for (const col of imagesToCheck) {
                if (col.mediaItem) {
                  const match = isProfileBulkClear
                    ? true
                    : this.getImageIdentifier(col.mediaItem, category) === targetIdentifier;
                  if (match) {
                    col.deleteCheckbox = false;
                    this.toggleDelete(s, col);
                    clearedCount++;
                  }
                }
              }

              processedCount++;
              this.applyDialog.percentage = (processedCount / totalStores) * 100;
              this.applyDialog.message = `選択を解除中... (${processedCount}/${totalStores}店舗、${clearedCount}件解除)`;
              await this.$nextTick();
            })
          );
        }

        this.applyDialog.percentage = 100;
        this.applyDialog.message = `選択解除が完了しました（${clearedCount}枚の画像選択を解除）`;
        this.applyDialog.button = true;
        this.bulkSelectedImages.splice(targetIndex, 1);
        await this.$nextTick();
      } catch (error) {
        console.error("選択解除処理でエラーが発生しました:", error);
        this.applyDialog.message = `選択解除処理でエラーが発生しました: ${
          error instanceof Error ? error.message : String(error)
        }`;
        this.applyDialog.button = true;
        this.applyDialog.errorMessages = [
          {
            message: error instanceof Error ? error.message : String(error),
            action: "選択解除",
            status: "unknown",
            store: "",
          },
        ];
        await this.$nextTick();
      } finally {
        this.isBulkSelecting = false;
      }
    },
    updateIsDirty() {
      for (const s of this.selectedAreaStores) {
        for (const c of s.columns) {
          if (c.manipulation === "ADD_IMAGE") {
            if (c.addCheckbox) {
              this.isDirty = true;
              return;
            }
          } else if (c.manipulation !== "NONE") {
            this.isDirty = true;
            return;
          }
        }
      }
      this.isDirty = false;
    },
    /**
     * 変更内容反映確認ダイアログを表示
     */
    showApplyConfirmDialog() {
      // 変更内容を集計
      const uploads: [StoreWithImages, MediaColumn][] = [];
      for (const store of this.selectedAreaStores) {
        for (const column of store.columns) {
          if (column.manipulation !== "NONE") {
            if (column.manipulation !== "ADD_IMAGE" || column.addCheckbox) {
              uploads.push([store, column]);
            }
          }
        }
      }

      // ADD_IMAGEの操作を画像ファイルごとにグループ化
      const addImageGroups: Map<string, [StoreWithImages, MediaColumn][]> = new Map();
      const deleteOperations: [StoreWithImages, MediaColumn][] = [];
      const otherOperations: [StoreWithImages, MediaColumn][] = [];

      for (const [store, column] of uploads) {
        if (column.manipulation === "ADD_IMAGE") {
          const key = column.mediaFile.key;
          if (!addImageGroups.has(key)) {
            addImageGroups.set(key, []);
          }
          addImageGroups.get(key)!.push([store, column]);
        } else if (column.manipulation === "DELETE") {
          deleteOperations.push([store, column]);
        } else {
          otherOperations.push([store, column]);
        }
      }

      // メッセージを生成
      const messages: string[] = [];
      const hasDelete = deleteOperations.length > 0;

      // 画像追加（全画像をまとめて集計）
      if (addImageGroups.size > 0) {
        const allAddStores = new Set<number>();
        let totalAddImages = 0;
        for (const [, imageUploads] of addImageGroups) {
          for (const [store] of imageUploads) {
            allAddStores.add(store.poiId);
          }
          totalAddImages++;
        }
        const addStoreCount = allAddStores.size;
        // 削除がない場合は「追加します」、削除がある場合は「追加」のみ
        const addSuffix = hasDelete ? "" : "します";
        if (addStoreCount === 1) {
          if (totalAddImages === 1) {
            const [, firstColumn] = Array.from(addImageGroups.values())[0][0];
            messages.push(`${firstColumn.mediaFile.file.name} を1店舗に追加${addSuffix}`);
          } else {
            messages.push(`${totalAddImages}枚の画像を1店舗に追加${addSuffix}`);
          }
        } else {
          if (totalAddImages === 1) {
            const [, firstColumn] = Array.from(addImageGroups.values())[0][0];
            messages.push(
              `${firstColumn.mediaFile.file.name} を${addStoreCount}店舗に追加${addSuffix}`
            );
          } else {
            messages.push(`${totalAddImages}枚の画像を${addStoreCount}店舗に追加${addSuffix}`);
          }
        }
      }

      // 画像削除
      if (deleteOperations.length > 0) {
        const uniqueStores = new Set(deleteOperations.map(([store]) => store.poiId));
        const storeCount = uniqueStores.size;
        const imageCount = deleteOperations.length;
        if (storeCount === 1) {
          messages.push(`${imageCount}枚の画像を1店舗から削除します`);
        } else {
          messages.push(`${imageCount}枚の画像を${storeCount}店舗から削除します`);
        }
      }

      // その他の操作（カテゴリ変更など）
      if (otherOperations.length > 0) {
        const uniqueStores = new Set(otherOperations.map(([store]) => store.poiId));
        const storeCount = uniqueStores.size;
        messages.push(`${otherOperations.length}件の変更を${storeCount}店舗に反映します`);
      }

      if (messages.length === 0) {
        return;
      }

      // メッセージを「、」で結合し、最後に「。よろしいですか？」を追加
      this.applyConfirmDialog.message = messages.join("、") + "。よろしいですか？";
      this.applyConfirmDialog.show = true;
    },
    /**
     * 確認ダイアログでOKが押されたときに実際の反映処理を実行
     */
    confirmApplyChanges() {
      this.applyConfirmDialog.show = false;
      this.applyChanges();
    },
    /**
     * 全画像削除確認ダイアログを表示
     */
    showDeleteAllImagesDialog() {
      this.deleteAllImagesDialog.confirmationText = "";
      this.deleteAllImagesDialog.show = true;
    },
    /**
     * 全画像を削除対象としてマークして反映処理を実行
     */
    async deleteAllImages() {
      this.deleteAllImagesDialog.show = false;
      this.deleteAllImagesDialog.confirmationText = "";

      // 進捗ダイアログを表示
      this.applyDialog.show = true;
      this.applyDialog.percentage = 0;
      this.applyDialog.title = "全画像を削除中";
      this.applyDialog.message = "画像データを読み込み中...";
      this.applyDialog.button = false;
      this.applyDialog.errorMessages = [];
      await this.$nextTick();

      try {
        // 全店舗の画像データを読み込む
        await this.loadStoreMediaDataIfNeeded("画像データを読み込み中...");

        // 全ての店舗の全ての画像を削除対象としてマーク
        let processedCount = 0;
        const totalStores = this.selectedAreaStores.length;
        for (const store of this.selectedAreaStores) {
          for (const column of store.columns) {
            if (column.manipulation === "ADD_IMAGE") {
              // 追加した画像は削除（表から削除）
              const index = store.columns.findIndex((col) => col.key === column.key);
              if (index >= 0) {
                store.columns.splice(index, 1);
              }
            } else {
              // 既存の画像は削除対象としてマーク
              column.deleteCheckbox = true;
              column.applyDeleteCheckbox();
            }
          }
          processedCount++;
          this.applyDialog.percentage = (processedCount / totalStores) * 100;
          this.applyDialog.message = `削除対象を選択中... (${processedCount}/${totalStores}店舗)`;
          await this.$nextTick();
        }

        this.updateIsDirty();

        // 反映処理を実行
        if (this.isDirty) {
          await this.applyChanges();
        } else {
          this.applyDialog.message = "削除対象の画像がありませんでした";
          this.applyDialog.button = true;
        }
      } catch (error) {
        console.error("全画像削除処理でエラーが発生しました:", error);
        this.applyDialog.message = `全画像削除処理でエラーが発生しました: ${
          error instanceof Error ? error.message : String(error)
        }`;
        this.applyDialog.button = true;
        this.applyDialog.errorMessages = [
          {
            message: error instanceof Error ? error.message : String(error),
            action: "全画像削除",
            status: "unknown",
            store: "",
          },
        ];
        await this.$nextTick();
      }
    },
    async applyChanges() {
      const sleep = (msec: number) => new Promise((resolve) => setTimeout(resolve, msec));
      this.applyDialog.show = true;
      this.applyDialog.percentage = 0;
      this.applyDialog.title = "変更内容を反映中";
      this.applyDialog.button = false;
      this.applyDialog.errorMessages.length = 0;

      const uploadedImages: { [key: string]: string } = {};
      const uploads: [StoreWithImages, MediaColumn][] = [];
      const uploadErrorMessages: ReflectionErrorMessage[] = [];

      // すべての操作を収集（ADD_IMAGEはチェックが付いているものだけ追加対象）
      for (const store of this.selectedAreaStores) {
        for (const column of store.columns) {
          if (column.manipulation !== "NONE") {
            if (column.manipulation !== "ADD_IMAGE" || column.addCheckbox) {
              uploads.push([store, column]);
            }
          }
        }
      }
      const poiGroupId = Number(this.poiGroupId);
      let count = 0;
      this.uploadProgression = uploads.length;

      // ADD_IMAGEの操作を画像ファイルとカテゴリの組み合わせでグループ化
      // 同じ画像でもカテゴリが異なる場合は別グループとして扱う
      const addImageGroups: Map<string, [StoreWithImages, MediaColumn][]> = new Map();
      // DELETEの操作を収集
      const deleteOperations: [StoreWithImages, MediaColumn][] = [];
      const otherOperations: [StoreWithImages, MediaColumn][] = [];

      for (const [store, column] of uploads) {
        if (column.manipulation === "ADD_IMAGE") {
          const key = `${column.mediaFile.key}_${column.category}`;
          if (!addImageGroups.has(key)) {
            addImageGroups.set(key, []);
          }
          addImageGroups.get(key)!.push([store, column]);
        } else if (column.manipulation === "DELETE") {
          deleteOperations.push([store, column]);
        } else {
          otherOperations.push([store, column]);
        }
      }

      // PROFILEの置き換え（同一反映でPROFILEの追加と旧ロゴ削除がある）では、Google側の競合を避けるため
      // 「旧PROFILE削除 → 追加 → その他の削除」の順にする。待機は入れない（順序のみで安定したため）。
      const hadProfileAddInThisApply = [...addImageGroups.values()].some(
        (imageUploads) => imageUploads[0]?.[1]?.category === "PROFILE"
      );
      const hasProfileDelete = deleteOperations.some(([, col]) => col.category === "PROFILE");
      const profileReplaceFlow = hadProfileAddInThisApply && hasProfileDelete;

      const profileDeleteOps = profileReplaceFlow
        ? deleteOperations.filter(([, col]) => col.category === "PROFILE")
        : [];
      const otherDeleteOps = profileReplaceFlow
        ? deleteOperations.filter(([, col]) => col.category !== "PROFILE")
        : deleteOperations;

      const profileAddGroups = new Map<string, [StoreWithImages, MediaColumn][]>();
      const otherAddGroups = new Map<string, [StoreWithImages, MediaColumn][]>();
      if (profileReplaceFlow) {
        for (const [k, v] of addImageGroups) {
          if (v[0]?.[1]?.category === "PROFILE") {
            profileAddGroups.set(k, v);
          } else {
            otherAddGroups.set(k, v);
          }
        }
      }

      const processAddImageGroups = async (
        groups: Map<string, [StoreWithImages, MediaColumn][]>
      ) => {
        for (const [imageKey, imageUploads] of groups) {
          if (this.selectedAreaStores.length === 0) {
            return;
          }

          const [, firstColumn] = imageUploads[0];
          const storeCount = imageUploads.length;
          const initialMessage =
            storeCount === 1
              ? `${firstColumn.mediaFile.file.name} を追加中`
              : `${firstColumn.mediaFile.file.name} を複数店舗に追加中`;
          this.applyDialog.message = initialMessage;
          await this.$nextTick();

          try {
            if (!uploadedImages[imageKey]) {
              const supres = await requiredAuth<StorageGMBMediaPostResponse>(
                "post",
                `${import.meta.env.VITE_APP_API_BASE}v1/companies/${this.poiGroupId}/startUpload`,
                getOperationLogParams(this.$route, "image-put")
              );
              uploadedImages[imageKey] = supres.data.getUrl;
              const putUrl = supres.data.putUrl;
              await axios.put(putUrl, await firstColumn.mediaFile.load(), {
                headers: { "Content-Type": firstColumn.mediaFile.file.type },
              });
            }

            const getUrl = uploadedImages[imageKey];
            const mediaItemRequest: MediaItemRequest = {
              sourceUrl: getUrl,
              locationAssociation: { category: firstColumn.category },
            };
            const poiIDs = imageUploads.map(([store]) => store.poiId);
            const uniquePoiIDs = Array.from(new Set(poiIDs));
            const uniqueStoreCount = uniquePoiIDs.length;
            const oplog = getOperationLogParams(this.$route, "image-put");
            const bulkResponse = await putMediaItemBulkWithRetry(
              poiGroupId,
              poiIDs,
              mediaItemRequest,
              oplog,
              (processed, total) => {
                const processedCount = count + processed;
                this.applyDialog.percentage = (processedCount / uploads.length) * 100;
                this.uploadProgression = uploads.length - processedCount + uploadErrorMessages.length;
                if (uniqueStoreCount === 1) {
                  this.applyDialog.message = `${firstColumn.mediaFile.file.name} を追加中`;
                } else {
                  const displayedProcessed = Math.min(
                    Math.round((processed / poiIDs.length) * uniqueStoreCount),
                    uniqueStoreCount
                  );
                  this.applyDialog.message = `${firstColumn.mediaFile.file.name} を複数店舗に追加中 (${displayedProcessed}/${uniqueStoreCount}店舗)`;
                }
              }
            );

            for (let i = 0; i < bulkResponse.results.length; i++) {
              const result = bulkResponse.results[i];
              if (i >= imageUploads.length) continue;
              const [store, column] = imageUploads[i];
              if (!store || !column) continue;

              const index = store.columns.findIndex((col) => col.key === column.key);

              if (result.status === "success" && result.mediaItem) {
                store.columns.splice(
                  index,
                  1,
                  MediaColumn.ofMediaItem(result.mediaItem as MediaItem)
                );
                this.loadedStores[store.poiId].loadedMediaItems = store.columns;
                this.loadedStores[store.poiId].updated = true;
              } else {
                const errorMessage: OneErrorMessage = {
                  reflectionErrorMessage: {
                    message: `${store.storeName}: ${result.error || "画像の追加に失敗しました"}`,
                    action: "ADD_IMAGE",
                    status: "failed",
                    store: `${store.storeCode} / ${store.storeName}`,
                  },
                  isDiscontinued: false,
                };
                uploadErrorMessages.push(errorMessage.reflectionErrorMessage);
              }

              count++;
              this.applyDialog.percentage = (count / uploads.length) * 100;
              this.uploadProgression = uploads.length - count + uploadErrorMessages.length;
              await this.$nextTick();
            }
          } catch (e) {
            console.error(`[ADD_IMAGE Batch Error]`, e);
            for (const [store, column] of imageUploads) {
              let ex: AxiosError = null;
              if (axios.isAxiosError(e)) {
                ex = e as AxiosError;
              } else if (e instanceof GbpResponseError) {
                ex = (e as GbpResponseError).toAxiosError();
              }
              const uploadErrorMessage: OneErrorMessage = this.makeErrorMessage(
                ex,
                column,
                store
              ) as OneErrorMessage;
              uploadErrorMessages.push(uploadErrorMessage.reflectionErrorMessage);
              if (uploadErrorMessage.isDiscontinued === true) {
                this.applyDialog.message = "";
                this.applyDialog.errorMessages = uploadErrorMessages;
                this.applyDialog.button = true;
                return;
              }

              count++;
              this.applyDialog.percentage = (count / uploads.length) * 100;
              this.uploadProgression = uploads.length - count + uploadErrorMessages.length;
              await this.$nextTick();
            }
          }
        }
      };

      const runDeleteBatch = async (ops: [StoreWithImages, MediaColumn][]) => {
        if (ops.length === 0) {
          return;
        }
        const uniqueStores = new Set(ops.map(([store]) => store.poiId));
        const totalImages = ops.length;
        const storeCount = uniqueStores.size;

        if (storeCount === 1) {
          this.applyDialog.message = `${totalImages}枚の画像を削除中`;
        } else {
          this.applyDialog.message = `複数店舗から${totalImages}枚の画像を削除中`;
        }
        await this.$nextTick();

        try {
          const deleteItems: DeleteMediaItemRequest[] = ops.map(([store, column]) => ({
            poiId: store.poiId,
            mediaName: column.mediaItem.name,
          }));

          const oplog = getOperationLogParams(this.$route, "image-put");
          const bulkDeleteResponse = await deleteMediaItemBulkWithRetry(
            poiGroupId,
            deleteItems,
            oplog,
            (processed, total) => {
              const processedCount = count + processed;
              this.applyDialog.percentage = (processedCount / uploads.length) * 100;
              this.uploadProgression = uploads.length - processedCount + uploadErrorMessages.length;
              if (storeCount === 1) {
                this.applyDialog.message = `${totalImages}枚の画像を削除中 (${processed}/${total}枚)`;
              } else {
                this.applyDialog.message = `複数店舗から${totalImages}枚の画像を削除中 (${processed}/${total}枚)`;
              }
            }
          );

          const storeColumnMap = new Map<string, { store: StoreWithImages; column: MediaColumn }>();
          for (const [store, column] of ops) {
            const key = `${store.poiId}:${column.mediaItem.name}`;
            storeColumnMap.set(key, { store, column });
          }

          for (let i = 0; i < bulkDeleteResponse.results.length; i++) {
            const result = bulkDeleteResponse.results[i];
            const deleteItem = deleteItems[i];
            const key = `${deleteItem.poiId}:${deleteItem.mediaName}`;
            const mapped = storeColumnMap.get(key);
            if (!mapped) continue;

            const { store, column } = mapped;
            const index = store.columns.findIndex((col) => col.key === column.key);

            if (result.status === "success") {
              if (index >= 0) {
                store.columns.splice(index, 1);
                this.loadedStores[store.poiId].loadedMediaItems = [...store.columns];
                this.loadedStores[store.poiId].updated = true;
              }
            } else {
              const errorMessage: OneErrorMessage = {
                reflectionErrorMessage: {
                  message: `${store.storeName}: ${result.error || "画像の削除に失敗しました"}`,
                  action: "DELETE",
                  status: "failed",
                  store: `${store.storeCode} / ${store.storeName}`,
                },
                isDiscontinued: false,
              };
              uploadErrorMessages.push(errorMessage.reflectionErrorMessage);
            }

            count++;
            this.applyDialog.percentage = (count / uploads.length) * 100;
            this.uploadProgression = uploads.length - count + uploadErrorMessages.length;
            await this.$nextTick();
          }

          await this.$nextTick();
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        } catch (e) {
          console.error(`[DELETE Batch Error]`, e);
          for (const [store, column] of ops) {
            let ex: AxiosError = null;
            if (axios.isAxiosError(e)) {
              ex = e as AxiosError;
            } else if (e instanceof GbpResponseError) {
              ex = (e as GbpResponseError).toAxiosError();
            }
            const uploadErrorMessage: OneErrorMessage = this.makeErrorMessage(
              ex,
              column,
              store
            ) as OneErrorMessage;
            uploadErrorMessages.push(uploadErrorMessage.reflectionErrorMessage);

            count++;
            this.applyDialog.percentage = (count / uploads.length) * 100;
            this.uploadProgression = uploads.length - count + uploadErrorMessages.length;
            await this.$nextTick();
          }
        }
      };

      if (profileReplaceFlow) {
        await runDeleteBatch(profileDeleteOps);
        await this.$nextTick();
        await processAddImageGroups(profileAddGroups);
        await processAddImageGroups(otherAddGroups);
        await runDeleteBatch(otherDeleteOps);
      } else {
        await processAddImageGroups(addImageGroups);
        await runDeleteBatch(deleteOperations);
      }

      // CHANGE_CATEGORYの処理（既存の処理）
      for (const [row, c] of otherOperations) {
        if (this.selectedAreaStores.length === 0) {
          return;
        }

        this.applyDialog.message = `${row.storeName} を更新中`;
        await this.$nextTick();

        const index = row.columns.findIndex((col) => col.key === c.key);
        try {
          // カテゴリの変更
          if (c.manipulation === "CHANGE_CATEGORY") {
            const data: MediaItem = {
              name: c.mediaItem.name,
              locationAssociation: { category: c.category },
            };
            const oplog = getOperationLogParams(this.$route, "image-put");
            const res = await retry(() => api.patchMediaItem(poiGroupId, row.poiId, data, oplog));
            row.columns.splice(index, 1, MediaColumn.ofMediaItem(res.data));
            // キャッシュの内容を更新
            this.loadedStores[row.poiId].loadedMediaItems = row.columns;
          }
          // 反映終わったら該当店舗に"updated"フラグを立てておく
          this.loadedStores[row.poiId].updated = true;
        } catch (e) {
          // retry は AxiosError または GbpResponseError を投げる。GbpResponseError の場合は AxiosError に変換してエラーメッセージを生成する。
          // これは既存のエラーメッセージ生成関数をそのまま使うことで、既存と同じ形式でエラーメッセージを生成するため。
          console.error(`[${c.manipulation} Error]`, e);
          let ex: AxiosError = null;
          if (axios.isAxiosError(e)) {
            ex = e as AxiosError;
          } else if (e instanceof GbpResponseError) {
            ex = (e as GbpResponseError).toAxiosError();
          }
          const uploadErrorMessage: OneErrorMessage = this.makeErrorMessage(
            ex,
            c,
            row
          ) as OneErrorMessage;
          uploadErrorMessages.push(uploadErrorMessage.reflectionErrorMessage);
          if (uploadErrorMessage.isDiscontinued === true) {
            // アップロード上限に達してる429エラーの場合はここで終了
            this.applyDialog.message = "";
            this.applyDialog.errorMessages = uploadErrorMessages;
            this.applyDialog.button = true;
            return;
          }
        }

        // プログレスバーを進捗させる
        count++;
        this.applyDialog.percentage = (count / uploads.length) * 100;
        this.uploadProgression = uploads.length - count + uploadErrorMessages.length;
        await this.$nextTick();
        await sleep(300);
      }

      // 追加対象から外していた（チェックを外した）ADD_IMAGEの行をテーブルから削除（削除反映時と同様に一覧を更新）
      for (const store of this.selectedAreaStores) {
        store.columns = store.columns.filter((col) => col.manipulation !== "ADD_IMAGE");
        this.loadedStores[store.poiId].loadedMediaItems = [...store.columns];
        this.loadedStores[store.poiId].updated = true;
      }

      this.updateIsDirty();
      if (uploadErrorMessages.length > 0) {
        // エラーがあった場合はエラーダイアログを出す
        this.applyDialog.message = "";
        this.applyDialog.errorMessages = uploadErrorMessages;
      } else {
        this.applyDialog.message = "完了しました";
      }
      this.applyDialog.button = true;
    },

    /** アップロードエラーのダイアログの中身生成 */
    makeErrorMessage(
      e: AxiosError,
      column?: MediaColumn,
      row?: StoreWithImages
    ): OneErrorMessage | ErrorWithGBP {
      // trueだったら反映時のダイアログ生成用 falseはget時のエラーメッセージ生成用
      const forReflection = column && row ? true : false;
      let action: string;
      if (forReflection) {
        action = `${column.manipulation}, ${column.category}, ${column.getThumbnail()}`;
      }

      if (forReflection && e?.response?.data == null) {
        // ネットワークエラーとかでdataが得られなかった場合
        return {
          isDiscontinued: true,
          reflectionErrorMessage: {
            message: "Google Business Profileへの反映に失敗しました。",
            action,
            status: "unknown",
            store: `${row.storeCode} / ${row.storeName}`,
            solutions: ["アップロードは中断されました。"],
          },
        };
      }

      // エラー情報をパース
      const errorInfo = parseGMBErrorDetails(e);

      if (!forReflection) {
        const errorWithGBP: ErrorWithGBP = {
          errorCode: errorInfo.errorCode,
          isGBPError: errorInfo.isGBPError,
        };
        return errorWithGBP;
      }

      this.updateIsDirty();
      // エラーメッセージを生成
      const errorMessages = getErrorMessage(errorInfo);
      let errorMessage: string = "";
      if (
        errorInfo.isRateLimitExceeded ||
        errorInfo.isGBPErrorAndErrorCodeIs400 ||
        errorInfo.isGBPErrorAndErrorCodeIs500
      ) {
        errorMessage = `${column.getThumbnail()}の反映に失敗しました。`;
      } else {
        errorMessage = "システムエラーが発生しました。";
      }
      return {
        isDiscontinued: errorMessages.isDiscontinued,
        reflectionErrorMessage: {
          message: errorMessage,
          action,
          status: errorInfo.status,
          reasons: errorMessages.reasons,
          store: `${row.storeCode}, ${row.storeName}`,
          solutions: [errorMessages.solution],
        },
      };
    },

    /** XLSX全件エクスポートやインポート時に対象ストアをまとめてロードする */
    async loadBatchOfStores(
      e: PointerEventInit | null,
      dividedStores?: StoreWithImages[][]
    ): Promise<void> {
      if (this.isLoading > 0) {
        // ボタン二度押し防止
        return;
      }
      let isImportMode: boolean;
      if (e === null && dividedStores) {
        isImportMode = true;
        useSnackbar().addSnackbarMessages({
          text: "インポート対象の全てのデータを取得しています。<br>そのまましばらくお待ちください。",
          color: "info",
          timeout: 2500,
        });
        await this.getStorePics("import", dividedStores);
      } else {
        isImportMode = false;
        useSnackbar().addSnackbarMessages({
          text: "選択したグループの全てのデータを取得しています。<br>そのまましばらくお待ちください。",
          color: "info",
          timeout: 2500,
        });
        await this.getStorePics("all");
      }
      const incomplete = this.checkCompletion(isImportMode);
      /** 一店舗に画像がたくさん登録されていて、分割でmediaItem読み込みが発生していた場合、その分が読み込み完了になるまでチェックし続ける必要がある
       * pageSize: 2500 設定ならば実運用上ではそのケースはほぼ発生しないと思われる
       */
      if (incomplete === true) {
        this.allLoadTImer = setInterval(() => {
          this.checkCompletion(isImportMode);
        }, 1000);
      }
    },
    checkCompletion(isImportMode: boolean): boolean {
      let incomplete = false;
      const stores = isImportMode ? this.targetStores : this.selectedAreaStores;
      if (!stores) {
        return;
      }
      for (const store of stores) {
        if (!this.loadedStores[store.poiId].loaded) {
          // 一個でも未完あったら走査終了
          incomplete = true;
          break;
        }
      }
      if (incomplete === false) {
        // 全データダウンロード出来たらexportする
        this.isLoading = 0;
        clearInterval(this.allLoadTImer);
        if (isImportMode) {
          // インポートモードだったらXlsxImportDialogのimportFileを叩く
          (this.$refs.xlsxImportDialog as InstanceType<typeof XlsxImportDialog>)?.importFile();
        } else {
          this.exportFile();
        }
      }
      return incomplete;
    },

    async exportFile() {
      const aoa = []; // Array of arrays
      // ヘッダ行を追加する
      const maxImageCount = this.selectedAreaStores.reduce((prev, curr) => {
        return Math.max(prev, curr.columns.length);
      }, 0);
      const columns: string[] = ["店舗ID", "店舗コード", "ビジネス名"];
      for (let i = 0; i < maxImageCount; i++) {
        columns.push("");
      }
      aoa.push(columns);
      // 店舗ごとの行を追加する
      for (const s of this.selectedAreaStores) {
        const arr = [];
        arr.push(s.poiId);
        arr.push(s.storeCode);
        arr.push(s.storeName);
        for (const column of s.columns) {
          if (column.manipulation === "DELETE") {
            arr.push("");
          } else if (column.mediaItem) {
            arr.push(`${column.category}: ${column.mediaItem.thumbnailUrl}`);
          } else {
            arr.push(`${column.category}: ${column.mediaFile.file.name}`);
          }
        }
        aoa.push(arr);
      }
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const buf: ArrayBuffer = XLSX.write(wb, {
        type: "array",
        bookType: "xlsx",
        bookSST: true,
      });
      const companyName = useIndexedDb().company.name;
      const datetime = dayjs().format("YYYYMMDD");
      const filename = `${wordDictionary.service.name}-店舗画像一覧エクスポート-${companyName}-${datetime}.xlsx`;
      saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
    },
    async importFile(e) {
      const file: File = e.target.files[0];
      if (!file) {
        return;
      }
      e.target.value = "";
      this.xlsxFile = file;
      this.showXlsxImportDialog = true;
    },
    abortImport(): void {
      clearInterval(this.allLoadTImer);
      this.showXlsxImportDialog = false;
      this.targetStores = null;
      this.isLoading = 0;
    },
    async importFileDialogFinish(
      deleteCols: { poiId: number; key: number }[],
      deleteCancelCols: { poiId: number; key: number }[],
      changeCols: { poiId: number; key: number; category: string }[],
      addCols: { poiId: number; category: string; thumbnail: string; mediaFile: MediaFile }[]
    ) {
      this.showXlsxImportDialog = false;

      // インポート内容を反映していく
      const find = (poiId: number, key: number): [StoreWithImages, MediaColumn] => {
        const store = this.selectedAreaStores.find((s) => s.poiId === poiId);
        if (!store) {
          return [null, null];
        }
        const col = store.columns.find((c) => c.key === key);
        return [store, col];
      };
      for (const deleteCol of deleteCols) {
        const [store, col] = find(deleteCol.poiId, deleteCol.key);
        col.deleteCheckbox = true;
        this.toggleDelete(store, col);
      }
      for (const changeCol of changeCols) {
        const [_, col] = find(changeCol.poiId, changeCol.key);
        col.category = changeCol.category;
        this.changeCategory(col);
      }
      for (const deleteCancelCol of deleteCancelCols) {
        const [store, col] = find(deleteCancelCol.poiId, deleteCancelCol.key);
        col.deleteCheckbox = false;
        this.toggleDelete(store, col);
      }
      for (const addCol of addCols) {
        await this.loadFile(addCol.mediaFile.file);
        this.imageDialog.selectedStores = [addCol.poiId];
        this.imageDialog.selectedCategory = addCol.category;
        await this.addImage();
      }
      // 役目が終わったのでnullにする
      this.targetStores = null;
    },
    /** 検索キーワードにマッチするか */
    checkStoreMatching(store: StoreWithImages): boolean {
      if (!this.searchWord || this.searchWord === "") {
        return true;
      }
      this.searchWord = this.searchWord?.trim();
      const searchWord = this.searchWord?.toLowerCase();
      // 店舗名でマッチするか
      if (store.storeName?.toLowerCase().includes(searchWord)) {
        return true;
      }
      // 企業IDでマッチするか
      if (this.searchWord === store.poiId.toString()) {
        return true;
      }
      // 店舗コードでマッチするか
      if (store.storeCode?.toLowerCase().includes(searchWord)) {
        return true;
      }

      return false;
    },
    /** XLSXインポート対象のストアデータを取得する */
    async getTargetStoreData(targetStoreIds: number[]): Promise<void> {
      const targetStores: StoreWithImages[] = [];
      for (const poiID of targetStoreIds) {
        const store = this.selectedAreaStores.find((store) => poiID === store.poiId);
        if (store) {
          targetStores.push(store);
        }
      }
      if (targetStores.length === 0) {
        // インポート対象の店舗が存在しなかったら
        (
          this.$refs.xlsxImportDialog as InstanceType<typeof XlsxImportDialog>
        ).changeLoadingStatus();
      } else {
        // pageSize単位でストア格納する
        // divideすると参照が無くなるのでcheckCompletionで使う様にコピーしとく
        this.targetStores = Object.assign([], targetStores);
        const dividedStores = this.divide(targetStores, this.pageSize);
        this.loadBatchOfStores(null, dividedStores);
      }
    },
    isValidJsonForGmbErrorDetails(jsonString: string): boolean {
      try {
        const _: GmbErrorDetails = JSON.parse(jsonString);
        return true; // パース成功
      } catch (error) {
        return false; // パース失敗
      }
    },
  },
});

function getGbpErrorMessage(statusCode: number): string {
  if (statusCode === 429) {
    return `アクセス試行回数が上限に達しました。<br>少し時間を置いてリロードして下さい。<br>エラーコード[${statusCode}]`;
  } else if (statusCode >= 400 && statusCode < 500) {
    return `システムエラーが発生しました。<br>少し時間を置いてリロードして下さい。<br>エラーコード[${statusCode}]`;
  } else if (statusCode >= 500) {
    return `ただいまGoogle側のサービスが一時的に利用できません。<br>少し時間を置いてリロードして下さい。<br>エラーコード[${statusCode}]`;
  } else {
    return `システムエラーが発生しました。<br>少し時間を置いてリロードして下さい。<br>エラーコード[${statusCode}]`;
  }
}
</script>

<style lang="scss" scoped>
:deep(.v-btn__content) {
  color: inherit;
}

button,
.history-button {
  box-shadow: none;
}

.upload-progression {
  margin-left: 1em;
}

.media-edit-header {
  display: flex;
  align-items: flex-end;
}

.area-selector {
  max-width: 600px;
  margin-right: 10px;
}

:deep(.v-select.v-select--chips .v-select__selections) {
  min-height: 0;
}

.searchbox {
  max-width: 300px;
  margin-left: 20px;
  margin-right: 10px;

  &.v-input--is-disabled {
    background-color: #d7d8d9;
  }
}

:deep(.v-text-field.v-input--dense:not(.v-text-field--outlined) input) {
  padding-bottom: 5px;
}
.filter-button {
  &.button.is-primary[disabled] {
    color: rgba(0, 0, 0, 0.26);
    background-color: #d7d8d9;
    opacity: 1;
    cursor: auto;

    &:hover {
      color: rgba(0, 0, 0, 0.26);
      background-color: #d7d8d9 !important;
    }
  }
}
.head-buttons {
  margin-top: 24px;
  display: flex;
}

.change-cancel-button {
  margin-left: 10px;
  margin-right: 20px;
}

.buttons {
  margin-top: 0.5rem;

  button {
    margin-right: 10px;

    &.reflection-button {
      margin-right: 20px;
    }
  }
}

.store-selector {
  display: flex;
}

.all-select {
  margin-left: 10px;
}

.scroll {
  overflow-y: scroll;
  height: 250px;
}

.table {
  overflow-x: scroll;
  overflow-y: scroll;
  margin: 0;
}

.v-card {
  box-shadow: none;
}

.dirty {
  background: #aff;
}

.imagetable {
  border-collapse: collapse;
  border: 1px 0 1px 1px solid #ccc;

  thead th {
    position: sticky;
    top: 0;
    background-color: #f0f0f0;
    z-index: var(--z-index-loading);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-collapse: collapse;
      border: 1px solid #ccc;
    }
  }

  thead th:nth-child(1),
  thead th:nth-child(2),
  thead th:nth-child(3) {
    /* 行内の他のセルより手前に表示する */
    z-index: calc(var(--z-index-loading) + 1);
  }

  th:nth-child(1),
  th:nth-child(2),
  th:nth-child(3),
  td:nth-child(1),
  td:nth-child(2),
  td:nth-child(3) {
    /* 横スクロール時に固定する */
    position: sticky;
    background-color: #f0f0f0;
    z-index: var(--z-index-loading);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-collapse: collapse;
      border: 1px solid #ccc;
    }
  }

  th:nth-child(1),
  td:nth-child(1) {
    min-width: 90px;
    max-width: 90px;
    left: 0;
  }

  th:nth-child(2),
  td:nth-child(2) {
    min-width: 120px;
    max-width: 120px;
    left: 90px;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 150px;
    left: 210px;
  }

  td {
    background-color: #fff;
  }

  td:last-child {
    border-right: 1px solid #ccc;
  }
}

.image-columns {
  .image-column-item {
    width: 300px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;

    .image-column-header {
      margin-bottom: 4px;
    }

    .image-column-controls {
      margin-bottom: 8px;

      .image-column-first-row {
        height: 48px;
        min-height: 48px;
        display: flex;
        align-items: center;
        margin-bottom: 4px;
      }

      .image-column-replace-placeholder {
        /* チェックボックス行と同じ高さを確保するための見えないスペーサー */
        font-size: 0;
        line-height: 0;
      }

      .image-column-message-row {
        min-height: 48px;
        margin-bottom: 4px;
      }

      .bulk-apply-btn {
        width: 100%;
        font-size: 12px;
        text-transform: none;
        white-space: normal;
        height: auto;
        padding: 8px 4px;
        line-height: 1.3;

        &:disabled {
          background-color: #f5f5f5 !important;
          color: #9e9e9e !important;
          border-color: #e0e0e0 !important;
        }
      }

      .bulk-apply-btn-green {
        background-color: #4caf50 !important;
        color: #ffffff !important;
        border: 1px solid #45a049 !important;

        &:hover:not(:disabled) {
          background-color: #66bb6a !important;
        }
      }

      .bulk-apply-btn-red {
        background-color: #ea4335 !important;
        color: #ffffff !important;
        border: 1px solid #d33b2c !important;

        &:hover:not(:disabled) {
          background-color: #f15c4f !important;
        }
      }
    }

    .image-column-thumbnail {
      margin-top: 8px;

      .theme--light.v-image {
        padding: 10px;
      }
    }
  }
  display: flex;
  gap: 10px;

  & > div {
    width: 300px;
    padding: 10px;

    .theme--light.v-image {
      padding: 10px;
    }
  }
}

.v-pagination {
  margin-bottom: 20px;
  padding: 0;
}

:deep() {
  .v-pagination {
    button {
      box-shadow: none;
    }
  }
}

:deep(.v-pagination__item--active) {
  cursor: auto;
}

:deep(.reflection-dialog) {
  .error-dialog {
    height: calc(75vh - 48px);
    overflow-y: auto;

    & > li {
      border-bottom: 1px dashed;
      padding-bottom: 1em;
      margin-bottom: 1em;

      &:last-of-type {
        border-bottom-style: none;
        padding-bottom: 0;
        margin-bottom: 0;
      }
    }

    .error-head {
      margin: 0 0 1em 0;
    }

    dl {
      margin-bottom: 1em;
    }

    dt {
      font-weight: bold;
    }

    .error-solution {
      padding: 0;
      margin: 0;
    }
  }

  .v-card__actions > .v-btn.v-btn {
    margin-left: auto;
  }
}

.uploading-dialog {
  line-height: 160%;
}

:deep(.upload .upload-draggable) {
  border-width: 2px;
  background-color: #eee;
  margin-top: 30px;

  strong {
    display: block;
    margin-bottom: 60px;
    line-height: 160%;
    font-size: 1.2em;
  }

  p {
    margin: 0 0 10px 0;
  }

  .section {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }
}

.acceptable-size-list {
  margin: 1em 0 0 0;

  p {
    display: inline-block;
    margin: 0 0 0.5em 0;
    padding-bottom: 2px;
    border-bottom: 4px solid #cdcdcd;
  }

  dl {
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: 190px auto 1fr;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
    text-align: left;
  }

  dt {
    margin-bottom: 10px;
    margin-right: 10px;
  }

  dd {
    margin-bottom: 10px;
    margin-left: 0;

    &:nth-of-type(2n - 1) {
      &::after {
        content: "〜";
        margin: 0 0.25em;
      }
    }
  }
}

.img-file-error {
  color: red;
  margin-top: 1em;
}

.invalid-image-size {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
// Invalid aspect ratio. Got: 800x781 (1.024328), valid ratio 1.777778.
